import type { DeliveryAssignment } from '@ohmyprime/p2p-shared';
import {
  P2P_DEADLINE_MS,
  MIN_SWARM_SIZE_PREFETCH,
  MIN_SWARM_SIZE_PLAYBACK,
  LIVE_SPECULATIVE_GRACE_MS,
  LIVE_PEER_CONNECT_WAIT_MS,
  LIVE_ORIGIN_LEADER_GRACE_MS,
  DEFAULT_SEGMENT_DURATION_MS,
  liveEdgeWaitMs,
  recoverBufferThresholdMs,
  offloadBufferThresholdMs,
  swarmOffloadBufferThresholdMs,
  targetLatencyMs,
  fetcherRelayFloorMs,
  canonicalSegmentId,
  segmentCacheKey,
  segmentSequence,
  isSeedPeerId,
  electOriginFetcherPeerId,
  electSeniorFetcherPeerId,
  electStickyFetcherPeerId,
} from '@ohmyprime/p2p-shared';
import type {
  P2PModeTransition,
  P2PPlaybackMode,
  PeerInfo,
  PendingSegmentRequest,
  PlaybackState,
} from './types.js';
import type { SegmentStore } from './segment-store.js';
import type { SegmentVerifier } from './segment-verifier.js';
import type { MetricsCollector } from './metrics-collector.js';
import { NO_ORIGIN, type OriginSource, type OriginFetchOptions } from './origin-source.js';

const BASE_MAX_PARALLEL = 3;
const PREFETCH_DEADLINE_MULTIPLIER = 2.5;
const STABLE_CONFIRMED_GRACE_MS = 350;
const LOW_LATENCY_CONFIRMED_GRACE_MS = 650;
const STABLE_ORIGIN_FALLBACK_MS = 800;

export type RequestFromPeerFn = (peerId: string, segmentId: string, urgent?: boolean) => boolean;
export type GetPeerConnectionFn = (
  peerId: string,
) => { recordSuccess(bytes: number, ms: number): void; recordFailure(): void; confirmSegmentReceived?(id: string, sha256: string): void } | undefined;

export interface SchedulerCallbacks {
  getDeliveryAssignment?: () => DeliveryAssignment | null;
  requestFromPeer: RequestFromPeerFn;
  getPeerConnection?: GetPeerConnectionFn;
  onSegmentFromPeer: (segmentId: string, peerId: string) => void;
  onSegmentFromOrigin: (segmentId: string) => void;
  onDeadlineMiss: (segmentId: string) => void;
  getSwarmSize?: () => number;
  getConnectedPeerCount?: () => number;
  getConnectedPeerIds?: () => string[];
  getLocalPeerId?: () => string;
  getLocalRegion?: () => string;
  onP2PSkip?: (reason: string, segmentId: string) => void;

  /** Diagnostics: fired once each time the playback mode flips (recover/stable/offload). */
  onModeChange?: (info: P2PModeTransition) => void;

  /** True for seed peers. Seeds are never fetcher candidates nor P2P targets (default: `seed-` prefix). */
  isSeedPeer?: (peerId: string) => boolean;

  /** Known SHA-256 for a segment (catalog), used to verify deliveries with no pending request. */
  getExpectedSha256?: (segmentId: string) => string | undefined;
  /** Tracker join time (ms) of a peer, including the local one; drives senior election. */
  getPeerJoinedAt?: (peerId: string) => number | undefined;
}

export interface SegmentRequestSpec {
  segmentId: string;
  /** SHA-256 hex of the encrypted segment bytes. */
  sha256: string;
}

export interface SchedulerOptions {
  transportVersion?: 1 | 2;
  /** Where segments come from when no viewer delivers in time (default: rejects). */
  originSource?: OriginSource;
  /** Fetcher election among viewers (default 'senior'). */
  fetcherElection?: 'hash' | 'sticky' | 'senior';
}

/**
 * Decides where to fetch each segment: local cache, P2P peers, or the origin (seed).
 * Handles deadline timers, adaptive parallelism, and prefetch.
 */
export class SegmentScheduler {
  private readonly store: SegmentStore;
  private readonly verifier: SegmentVerifier;
  private readonly metrics: MetricsCollector;
  private readonly callbacks: SchedulerCallbacks;
  private readonly p2pDeadlineMs: number;
  private readonly segmentDurationMs: number;
  private readonly origin: OriginSource;
  private readonly fetcherElection: 'hash' | 'sticky' | 'senior';
  private readonly transportVersion: 1 | 2;
  private readonly assignedRequests = new WeakMap<PendingSegmentRequest, { active: string | null; attempted: Set<string>; background: boolean }>();
  private p2pEnabled: boolean;

  private readonly peers: Map<string, PeerInfo> = new Map();
  private readonly pending: Map<string, PendingSegmentRequest> = new Map();
  private catalogEpoch = 0;
  // Start at pending creation so reactive/relay wait contributes conservatively.
  private readonly requestStartedAt = new WeakMap<PendingSegmentRequest, number>();
  private readonly blacklistedPeers: Set<string> = new Set();
  private readonly reactiveRequested: Map<string, Set<string>> = new Map();
  private readonly prefetchInFlight: Set<string> = new Set();
  private readonly uploadFetchInFlight: Set<string> = new Set();
  private readonly uploadFetchDone: Map<string, Promise<void>> = new Map();
  /** Cache keys filled by early P2P delivery before playback requested the segment */
  private readonly p2pCacheSources: Map<string, string> = new Map();
  private playbackState: Required<PlaybackState> = {
    bufferHealthMs: 0,
    latencyToLiveEdgeMs: 0,
    lastLiveEdgeNudgeAtMs: 0,
  };
  private renditionId = '';

  constructor(
    store: SegmentStore,
    verifier: SegmentVerifier,
    metrics: MetricsCollector,
    callbacks: SchedulerCallbacks,
    p2pDeadlineMs = P2P_DEADLINE_MS,
    p2pEnabled = true,
    segmentDurationMs = DEFAULT_SEGMENT_DURATION_MS,
    ultraLowLatency = false,
    options: SchedulerOptions = {},
  ) {
    this.store = store;
    this.verifier = verifier;
    this.metrics = metrics;
    this.callbacks = callbacks;
    this.p2pDeadlineMs = p2pDeadlineMs;
    this.segmentDurationMs = segmentDurationMs;
    this.p2pEnabled = p2pEnabled;
    this.ultraLowLatency = ultraLowLatency;
    this.origin = options.originSource ?? NO_ORIGIN;
    this.transportVersion = options.transportVersion ?? 1;
    this.fetcherElection = options.fetcherElection ?? 'senior';
  }

  private ultraLowLatency = false;
  /** Diagnostics: last mode reported to onModeChange, to emit only on real flips. */
  private lastMode: P2PPlaybackMode | null = null;
  /** Timestamp of the most recent live-edge nudge reported by the player. */
  private lastNudgeAtMs = 0;

  setEnabled(enabled: boolean): void {
    this.p2pEnabled = enabled;
  }

  setPlaybackState(state: PlaybackState): void {
    if (
      typeof state.bufferHealthMs === 'number' &&
      Number.isFinite(state.bufferHealthMs)
    ) {
      this.playbackState.bufferHealthMs = Math.max(0, state.bufferHealthMs);
    }
    if (
      typeof state.latencyToLiveEdgeMs === 'number' &&
      Number.isFinite(state.latencyToLiveEdgeMs)
    ) {
      this.playbackState.latencyToLiveEdgeMs = Math.max(
        0,
        state.latencyToLiveEdgeMs,
      );
    }
    if (
      typeof state.lastLiveEdgeNudgeAtMs === 'number' &&
      state.lastLiveEdgeNudgeAtMs > this.lastNudgeAtMs
    ) {
      this.lastNudgeAtMs = state.lastLiveEdgeNudgeAtMs;
    }
  }

  setRendition(renditionId: string): void {
    this.renditionId = renditionId;
  }

  /** Rendition used for segment cache keys (v1: always `main`). */
  getRenditionId(): string {
    return this.renditionId;
  }

  private cacheKey(segmentId: string, sha256: string): string {
    const base = segmentCacheKey(this.renditionId, segmentId);
    return sha256 ? `${base}#${sha256.toLowerCase()}` : base;
  }

  private isSeed(peerId: string): boolean {
    return this.callbacks.isSeedPeer?.(peerId) ?? isSeedPeerId(peerId);
  }

  /**
   * Elect the viewer that pulls `segmentId` from the seed and relays it.
   * Seeds are never candidates. Senior election always picks the viewer that
   * joined first (join times from the tracker); sticky keeps one fetcher for a
   * window of consecutive segments; `init.mp4` (no sequence) always uses the hash.
   */
  electFetcherPeerId(segmentId: string, candidates: string[]): string {
    const pool = [...new Set(candidates)].filter((pid) => !this.isSeed(pid));
    if (this.fetcherElection === 'senior' && segmentSequence(segmentId) !== null) {
      return electSeniorFetcherPeerId(pool, (pid) => this.callbacks.getPeerJoinedAt?.(pid));
    }
    if (this.fetcherElection === 'sticky') {
      const seq = segmentSequence(segmentId);
      if (seq !== null) return electStickyFetcherPeerId(seq, pool);
    }
    return electOriginFetcherPeerId(segmentId, pool);
  }

  updatePeer(info: PeerInfo, newSegmentId?: string): void {
    const canonicalId = newSegmentId
      ? canonicalSegmentId(newSegmentId)
      : undefined;
    const normalized: PeerInfo = {
      ...info,
      availableSegments: new Set(
        Array.from(info.availableSegments).map((s) => canonicalSegmentId(s)),
      ),
    };
    this.peers.set(normalized.peerId, normalized);

    if (canonicalId && this.transportVersion === 2) {
      this.retryAssignedPeer(normalized.peerId);
      return;
    }
    if (canonicalId) {
      // Reactively ask a peer that just announced it HAS a pending segment, in any
      // mode including recover — a confirmed holder is exactly what helps the
      // starved buffer recover (the playback race uses origin grace 0 in recover).
      const req = this.findPendingRequest(canonicalId);
      if (req && !req.fulfilled) {
        let sent = this.reactiveRequested.get(canonicalId);
        if (!sent) {
          sent = new Set();
          this.reactiveRequested.set(canonicalId, sent);
        }
        if (!sent.has(normalized.peerId)) {
          sent.add(normalized.peerId);
          this.callbacks.requestFromPeer(normalized.peerId, canonicalId, true); // reactive for pending = urgent
        }
      }
    }
  }

  removePeer(peerId: string): void {
    this.peers.delete(peerId);
    if (this.transportVersion === 2) for (const req of this.pending.values()) {
      if (this.assignedRequests.get(req)?.active === peerId) this.advanceAssigned(req);
    }
  }

  /** Retry open segment requests when a peer's WebRTC data channel becomes ready. */
  onPeerConnected(peerId: string): void {
    if (this.blacklistedPeers.has(peerId)) return;
    if (this.transportVersion === 2) { this.retryAssignedPeer(peerId); return; }
    // Ask a freshly-connected peer for in-flight segments in any mode, including
    // recover — getting another P2P source online is what helps the buffer recover.
    this.retryPendingPeerRequestsForPeer(peerId);
  }

  private retryPendingPeerRequestsForPeer(peerId: string): void {
    for (const req of this.pending.values()) {
      if (req.fulfilled) continue;
      const id = canonicalSegmentId(req.segmentId);
      let sent = this.reactiveRequested.get(id);
      if (!sent) {
        sent = new Set();
        this.reactiveRequested.set(id, sent);
      }
      if (!sent.has(peerId)) {
        sent.add(peerId);
        this.callbacks.requestFromPeer(peerId, id, true); // on connect for pending = urgent
      }
    }
  }

  /** Retry all pending segment requests once a reliable data channel opens. */
  onPeerDataChannelReady(): void {
    if (this.transportVersion === 2) {
      for (const id of this.callbacks.getConnectedPeerIds?.() ?? []) this.retryAssignedPeer(id);
      return;
    }
    this.retryPendingPeerRequests();
  }

  /**
   * Proactively fetch upcoming segments (non-blocking for playback).
   */
  prefetchSegments(specs: SegmentRequestSpec[]): void {
    if (!this.p2pEnabled) return;
    if (this.transportVersion === 2) {
      for (const spec of specs.slice(0, 2)) void this.ensureOriginForUpload(spec.segmentId, spec.sha256).catch(() => {});
      return;
    }
    const mode = this.playbackMode();
    const signalingSwarm = this.callbacks.getSwarmSize?.() ?? 0;
    const connectedCount = this.callbacks.getConnectedPeerCount?.() ?? 0;
    const swarmSize = Math.max(signalingSwarm, connectedCount, this.peers.size);
    if (swarmSize < MIN_SWARM_SIZE_PLAYBACK) return;

    // Lever B in recover: previously prefetch was disabled entirely while the
    // buffer was starved — but that is exactly when the swarm has nothing cached
    // ahead and every segment falls to origin, so disabling it cemented 0%
    // offload during the recover episodes that dominate a thin live edge. Now we
    // keep the *elected origin-fetcher's* proactive seed running in recover (so
    // peers can get cache hits), but: (1) bound it to a single segment ahead to
    // avoid piling origin load on an already-starved pipe, and (2) skip the
    // non-fetcher speculative prefetch race, which in recover would only add
    // redundant origin contention without helping our own buffer.
    const recover = mode === 'recover';
    const work = recover ? specs.slice(0, 1) : specs;

    for (const spec of work) {
      const segmentId = canonicalSegmentId(spec.segmentId);
      const key = this.cacheKey(segmentId, spec.sha256);
      if (this.store.has(key) || this.prefetchInFlight.has(key)) {
        continue;
      }
      if (this.pending.has(key)) continue;

      // Offload lever B: if THIS peer is the elected origin-fetcher for an
      // upcoming segment, pull it from origin now and cache it so peers get it
      // instantly over P2P — instead of every peer racing origin at playback
      // time. Election is deterministic per segment, so across the swarm each
      // segment is proactively fetched by ~one peer (origin load ≈ 1× per
      // segment, spread out), which is what drives offload toward its ceiling.
      const connectedIds = this.callbacks.getConnectedPeerIds?.() ?? [];
      if (
        connectedIds.length > 0 &&
        this.isLocalOriginFetcher(segmentId, connectedIds)
      ) {
        void this.ensureOriginForUpload(segmentId, spec.sha256);
        continue;
      }

      // In recover, only the elected fetcher seeds (handled above). Non-fetchers
      // racing origin here would just stampede a starved origin without helping
      // their own buffer — defer to the seed + the urgent playback path instead.
      if (recover) continue;

      const { confirmed, speculative } = this.selectPeers(segmentId);
      const peerIds = confirmed.length > 0 ? confirmed : speculative;
      if (peerIds.length === 0) continue;

      this.prefetchInFlight.add(key);
      void this.raceP2PAndOrigin(
        key,
        segmentId,
        spec.sha256,
        this.p2pRequestTargets(segmentId, confirmed, speculative),
        confirmed,
        speculative,
        true, // isPrefetch
        mode,
      )
        .catch(() => undefined)
        .finally(() => {
          this.prefetchInFlight.delete(key);
        });
    }
  }

  async requestSegment(
    segmentId: string,
    sha256: string,
  ): Promise<ArrayBuffer> {
    const id = canonicalSegmentId(segmentId);
    const key = this.cacheKey(id, sha256);
    const epoch = this.catalogEpoch;

    // Fase 1 instrumentation
    this.metrics.markRequestStart(id);

    const cached = this.store.get(key);
    if (cached !== undefined) {
      const valid = sha256 && (await this.verifier.verify(cached, sha256));
      if (!this.catalogIsCurrent(epoch, id, sha256)) throw new Error('Playback catalogue changed');
      if (valid) {
        const fromPeerId = this.p2pCacheSources.get(key);
        if (fromPeerId) {
          this.p2pCacheSources.delete(key);
          const fromSeed = this.isSeed(fromPeerId);
          this.metrics.recordSegment({
            segmentId: id,
            source: fromSeed ? 'seed' : 'peer',
            durationMs: 0,
            bytesReceived: cached.byteLength,
            fromPeerId,
            playback: true,
          });
          if (fromSeed) this.callbacks.onSegmentFromOrigin(id);
          else this.callbacks.onSegmentFromPeer(id, fromPeerId);
        }
        return cached;
      }
      this.store.delete(key);
      this.p2pCacheSources.delete(key);
    }

    if (this.transportVersion === 2) return this.requestAssigned(key, id, sha256, false);

    await this.waitForConnectedPeers();

    const inflight = this.pending.get(key);
    if (inflight !== undefined) {
      return new Promise<ArrayBuffer>((resolve, reject) => {
        const orig = inflight.resolve;
        inflight.resolve = (data: ArrayBuffer) => {
          orig(data);
          resolve(data);
        };
        const origRej = inflight.reject;
        inflight.reject = (err: Error) => {
          origRej(err);
          reject(err);
        };
      });
    }

    const signalingSwarm = this.callbacks.getSwarmSize?.() ?? this.peers.size;
    const connectedCount = this.callbacks.getConnectedPeerCount?.() ?? 0;
    const p2pActive =
      this.p2pEnabled &&
      (connectedCount >= MIN_SWARM_SIZE_PLAYBACK ||
        signalingSwarm >= MIN_SWARM_SIZE_PLAYBACK);

    if (!this.p2pEnabled) {
      this.callbacks.onP2PSkip?.('p2p disabled', id);
    } else if (p2pActive) {
      const mode = this.playbackMode();
      const { confirmed, speculative } = this.selectPeers(id);
      const effectiveConfirmed = confirmed;
      const requestTargets = this.p2pRequestTargets(
        id,
        effectiveConfirmed,
        speculative,
      );
      const isLocalFetcher = this.isLocalOriginFetcher(id, [
        ...effectiveConfirmed,
        ...speculative,
      ]);

      if (
        requestTargets.length > 0 ||
        effectiveConfirmed.length > 0 ||
        speculative.length > 0 ||
        isLocalFetcher
      ) {
        // Live playback request → urgent downstream. Crucially we ALSO race in
        // 'recover' mode now: previously recover skipped P2P entirely, which was
        // self-defeating — when the buffer is starved because origin is slow, a
        // peer that already holds the segment (or the elected fetcher relaying it)
        // can deliver FASTER than origin and actually help the buffer recover.
        // In recover the origin grace is 0 (raceP2PAndOrigin), so origin still
        // fires immediately in parallel — P2P is pure upside, never a delay.
        return this.raceP2PAndOrigin(
          key,
          id,
          sha256,
          requestTargets,
          effectiveConfirmed,
          speculative,
          false,
          mode,
        );
      }
      this.callbacks.onP2PSkip?.(
        speculative.length > 0 ? 'no confirmed peer' : 'no peers',
        id,
      );
    } else {
      this.callbacks.onP2PSkip?.('no peers', id);
    }

    // Root cause fix for "no peers" thundering herd (the amplifier of every
    // stall in the 11:39:12+ log): when p2pActive=false we used to call
    // fetchFromOrigin directly WITHOUT registering in this.pending. Any
    // concurrent requestSegment() for the same key (players do this aggressively
    // on bufferStalledError, after recoverMediaError, or during slow ~2.5s
    // origin loads > segment duration) would also take the direct path,
    // spawning N parallel HTTP GETs for the identical .m4s. This overloaded
    // the origin (readFile from live-writing dir), made every fetch 2.5s+,
    // starved the MSE buffer, and produced the repeated bufferStalledError +
    // duplicate "Got" + "Requesting" spam visible in the log.
    //
    // Now: always create a pending entry for the origin-only case so the
    // top-level inflight check catches duplicates and they share the single
    // fetch (exactly like the raceP2PAndOrigin path).
    const existing = this.pending.get(key);
    if (existing && !existing.fulfilled) {
      return new Promise<ArrayBuffer>((resolve, reject) => {
        const orig = existing.resolve;
        existing.resolve = (data: ArrayBuffer) => {
          orig(data);
          resolve(data);
        };
        const origRej = existing.reject;
        existing.reject = (err: Error) => {
          origRej(err);
          reject(err);
        };
      });
    }

    return new Promise<ArrayBuffer>((resolve, reject) => {
      const req: PendingSegmentRequest = {
        segmentId: id,
        sha256,
        resolve: (data: ArrayBuffer) => {
          this.pending.delete(key);
          this.reactiveRequested.delete(id);
          resolve(data);
        },
        reject: (err: Error) => {
          this.pending.delete(key);
          this.reactiveRequested.delete(id);
          reject(err);
        },
        deadlineTimer: undefined as unknown as ReturnType<typeof setTimeout>,
        originAbort: new AbortController(),
        fulfilled: false,
      };
      this.requestStartedAt.set(req, Date.now());
      this.pending.set(key, req);

      void this.fetchFromOrigin(key, id, sha256, req.originAbort!.signal)
        .then(() => {
          // fetchFromOrigin's internal post-processing already called our
          // hijacked req.resolve (which delivered to outer promises + deleted
          // from pending). We only need to mark fulfilled to prevent any
          // late double-calls.
          req.fulfilled = true;
        })
        .catch((err) => {
          if (!req.fulfilled) {
            req.fulfilled = true;
            req.reject(err instanceof Error ? err : new Error(String(err)));
          }
        });
    });
  }

  async handlePeerSegment(
    segmentId: string,
    data: ArrayBuffer,
    fromPeerId: string,
  ): Promise<void> {
    const id = canonicalSegmentId(segmentId);
    const req = this.findPendingRequest(id);
    const expectedSha =
      req?.sha256 || this.callbacks.getExpectedSha256?.(id) || '';
    // Nothing to verify against (unsolicited, or its catalog entry was pruned):
    // discard without scoring the peer, the data may well be valid.
    if (!expectedSha) return;
    const key = this.cacheKey(id, expectedSha);
    const epoch = this.catalogEpoch;

    const receivedAt = Date.now();
    // A seed answers origin requests, which start after the grace wait for peers: that wait is not transfer time.
    const requestedAt = req
      ? (this.isSeed(fromPeerId) && req.originStartedAt !== undefined ? req.originStartedAt : this.requestStartedAt.get(req))
      : undefined;
    // Unknown/unsolicited deliveries have no measured receive duration.
    const durationMs = requestedAt === undefined ? 0 : Math.max(0, receivedAt - requestedAt);
    const valid = await this.verifier.verify(data, expectedSha);
    if (!this.catalogIsCurrent(epoch, id, expectedSha)) {
      this.metrics.recordNetworkWaste('canceled', data.byteLength);
      return;
    }
    const conn = this.callbacks.getPeerConnection?.(fromPeerId);

    if (!valid) {
      this.metrics.recordNetworkWaste('invalid', data.byteLength);
      this.blacklistedPeers.add(fromPeerId);
      conn?.recordFailure();
      return;
    }

    if (this.store.has(key)) this.metrics.recordNetworkWaste('duplicate', data.byteLength);
    conn?.confirmSegmentReceived?.(id, expectedSha);
    if (durationMs > 0) conn?.recordSuccess(data.byteLength, durationMs);

    this.store.set(key, data);

    if (!req || req.fulfilled) {
      this.p2pCacheSources.set(key, fromPeerId);
      return;
    }

    const isBackgroundPull = this.assignedRequests.get(req)?.background ??
      (this.prefetchInFlight.has(key) || this.uploadFetchInFlight.has(key));
    if (isBackgroundPull) this.p2pCacheSources.set(key, fromPeerId);

    if (req.graceTimer) clearTimeout(req.graceTimer);
    req.fulfilled = true;
    clearTimeout(req.deadlineTimer);
    this.pending.delete(key);
    this.reactiveRequested.delete(id);
    req.originAbort?.abort();

    if (!isBackgroundPull) {
      const fromSeed = this.isSeed(fromPeerId);
      this.metrics.recordSegment({
        segmentId: id,
        source: fromSeed ? 'seed' : 'peer',
        durationMs,
        bytesReceived: data.byteLength,
        fromPeerId,
        playback: true,
      });
      this.metrics.markRequestDelivered(id, fromSeed ? 'seed' : 'peer');
      if (fromSeed) this.callbacks.onSegmentFromOrigin(id);
      else this.callbacks.onSegmentFromPeer(id, fromPeerId);
    } else if (this.transportVersion === 2) {
      if (this.isSeed(fromPeerId)) this.callbacks.onSegmentFromOrigin(id);
      else this.callbacks.onSegmentFromPeer(id, fromPeerId);
    }
    req.resolve(data);
  }

  /**
   * Fetch a segment from the origin (seed) immediately to serve pending peer upload requests.
   */
  ensureOriginForUpload(
    segmentId: string,
    sha256: string,
  ): Promise<void> {
    const id = canonicalSegmentId(segmentId);
    const key = this.cacheKey(id, sha256);

    if (this.store.has(key)) {
      return Promise.resolve();
    }

    if (this.transportVersion === 2) return this.requestAssigned(key, id, sha256, true).then(() => {});

    const existing = this.uploadFetchDone.get(key);
    if (existing) {
      return existing;
    }

    const inflight = this.pending.get(key);
    if (inflight && !inflight.fulfilled) {
      this.accelerateOriginFetch(key, inflight);
      const waitPlayback = new Promise<void>((resolve, reject) => {
        const origResolve = inflight.resolve;
        const origReject = inflight.reject;
        inflight.resolve = (data: ArrayBuffer) => {
          origResolve(data);
          resolve();
        };
        inflight.reject = (err: Error) => {
          origReject(err);
          reject(err);
        };
      });
      this.uploadFetchDone.set(key, waitPlayback);
      void waitPlayback.finally(() => {
        this.uploadFetchDone.delete(key);
      });
      return waitPlayback;
    }

    if (this.uploadFetchInFlight.has(key)) {
      return this.uploadFetchDone.get(key) ?? Promise.resolve();
    }
    this.uploadFetchInFlight.add(key);

    // Route upload pulls through the same race/pending path as playback so a
    // segment needed for peer upload and for local playback share one origin
    // fetch (fixes duplicate segment-from-origin events in logs).
    const fetchPromise = this.raceP2PAndOrigin(
      key,
      id,
      sha256,
      [],
      [],
      [],
      true,
      this.playbackMode(),
    );

    const done = fetchPromise
      .catch(() => undefined)
      .finally(() => {
        this.uploadFetchInFlight.delete(key);
        this.uploadFetchDone.delete(key);
      }) as Promise<void>;
    this.uploadFetchDone.set(key, done);
    return done;
  }

  cancelAll(): void {
    this.catalogEpoch++;
    for (const req of this.pending.values()) {
      req.fulfilled = true;
      if (req.graceTimer) clearTimeout(req.graceTimer);
      clearTimeout(req.deadlineTimer);
      req.originAbort?.abort();
      req.reject(new Error('Scheduler cancelled'));
    }
    this.pending.clear();
    this.reactiveRequested.clear();
    this.prefetchInFlight.clear();
    this.uploadFetchInFlight.clear();
    this.uploadFetchDone.clear();
    this.p2pCacheSources.clear();
  }

  getPeerCount(): number {
    return this.peers.size;
  }

  /** A v2 request has one active provider; backup is activated only by failure/deadline. */
  private requestAssigned(key: string, id: string, hash: string, background: boolean): Promise<ArrayBuffer> {
    const existing = this.pending.get(key);
    if (existing && !existing.fulfilled) {
      const state = this.assignedRequests.get(existing);
      if (state && !background) state.background = false;
      return new Promise((resolve, reject) => {
        const priorResolve = existing.resolve, priorReject = existing.reject;
        existing.resolve = data => { priorResolve(data); resolve(data); };
        existing.reject = error => { priorReject(error); reject(error); };
      });
    }
    return new Promise((resolve, reject) => {
      const finish = () => {
        clearTimeout(req.deadlineTimer); clearTimeout(req.graceTimer);
        req.fulfilled = true; req.originAbort?.abort();
        if (this.pending.get(key) === req) this.pending.delete(key);
        this.reactiveRequested.delete(id);
      };
      const req: PendingSegmentRequest = {segmentId: id, sha256: hash, fulfilled: false,
        deadlineTimer: undefined as unknown as ReturnType<typeof setTimeout>,
        resolve: data => { finish(); resolve(data); }, reject: error => { finish(); reject(error); }};
      this.requestStartedAt.set(req, Date.now());
      this.assignedRequests.set(req, {active: null, attempted: new Set(), background});
      this.pending.set(key, req);
      // The transport profile keeps a bounded peer budget. A thin reported buffer
      // shortens it to leave time for origin; no reported buffer is not evidence of a stall.
      const buffer = this.playbackState.bufferHealthMs;
      const peerBudget = Math.max(0, Math.min(this.p2pDeadlineMs, 600, buffer > 0 ? buffer - 200 : Infinity));
      req.deadlineTimer = setTimeout(() => {
        this.callbacks.onDeadlineMiss(id);
        req.reject(new Error(`Segment ${id} timed out from assigned providers and origin`));
      }, peerBudget + Math.max(1500, this.p2pDeadlineMs));
      const assignment = this.callbacks.getDeliveryAssignment?.();
      if (!this.p2pEnabled || !assignment || assignment.originFetcher || !assignment.primaryPeerId) {
        void this.startOriginFetch(key, req, background);
        return;
      }
      const state = this.assignedRequests.get(req)!;
      state.active = assignment.primaryPeerId;
      // Wait briefly for an assigned connection; an unavailable channel is not a NACK.
      this.requestAssignedPeer(req);
      const primaryBudget = assignment.backupPeerId ? Math.floor(peerBudget / 2) : peerBudget;
      req.graceTimer = setTimeout(() => {
        this.advanceAssigned(req);
        if (!req.fulfilled && !req.originFetchStarted) {
          req.graceTimer = setTimeout(() => void this.startOriginFetch(key, req, state.background), peerBudget - primaryBudget);
        }
      }, primaryBudget);
    });
  }

  private requestAssignedPeer(req: PendingSegmentRequest): boolean {
    const state = this.assignedRequests.get(req);
    if (!state?.active || req.fulfilled || req.originFetchStarted || this.blacklistedPeers.has(state.active)) return false;
    let sent = this.reactiveRequested.get(req.segmentId);
    if (!sent) { sent = new Set(); this.reactiveRequested.set(req.segmentId, sent); }
    if (sent.has(state.active)) return false;
    if (!this.callbacks.requestFromPeer(state.active, req.segmentId, !state.background)) return false;
    sent.add(state.active);
    return true;
  }
  private retryAssignedPeer(id: string): void {
    for (const req of this.pending.values()) if (this.assignedRequests.get(req)?.active === id) this.requestAssignedPeer(req);
  }
  private advanceAssigned(req: PendingSegmentRequest): void {
    const state = this.assignedRequests.get(req);
    if (!state || req.fulfilled || req.originFetchStarted) return;
    if (state.active) state.attempted.add(state.active);
    const a = this.callbacks.getDeliveryAssignment?.();
    const next = a?.originFetcher ? undefined : [a?.primaryPeerId, a?.backupPeerId]
      .find(id => id && !state.attempted.has(id) && !this.blacklistedPeers.has(id));
    state.active = next ?? null;
    if (next) this.requestAssignedPeer(req);
    else { clearTimeout(req.graceTimer); void this.startOriginFetch(this.cacheKey(req.segmentId, req.sha256), req, state.background); }
  }
  refreshDeliveryAssignment(): void {
    if (this.transportVersion !== 2) return;
    const a = this.callbacks.getDeliveryAssignment?.();
    for (const req of this.pending.values()) {
      const state = this.assignedRequests.get(req);
      if (state && state.active !== a?.primaryPeerId && state.active !== a?.backupPeerId) this.advanceAssigned(req);
    }
  }

  private adaptiveParallelLimit(peerIds: string[]): number {
    if (peerIds.length === 0) return BASE_MAX_PARALLEL;
    const top = this.peers.get(peerIds[0]!);
    if (!top) return BASE_MAX_PARALLEL;
    const speed = top.uploadRate;
    if (speed > 500_000) return 5;
    if (speed > 100_000) return 4;
    return BASE_MAX_PARALLEL;
  }

  /** Brief wait when signaling reports peers but WebRTC is still connecting. */
  private async waitForConnectedPeers(): Promise<void> {
    if (this.playbackMode() === 'recover') return;
    if ((this.callbacks.getConnectedPeerCount?.() ?? 0) > 0) return;
    const swarm = this.callbacks.getSwarmSize?.() ?? 0;
    if (swarm === 0) return;

    const deadline = Date.now() + LIVE_PEER_CONNECT_WAIT_MS;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 50));
      if ((this.callbacks.getConnectedPeerCount?.() ?? 0) > 0) return;
    }
  }

  private adaptiveGraceMs(peerIds: string[]): number {
    if (peerIds.length === 0) return LIVE_SPECULATIVE_GRACE_MS; // Fase 1: use the tuned constant
    const top = this.peers.get(peerIds[0]!);
    const rtt = top?.rttMs ?? 100;
    const tuned = this.metrics.getTunedDeadlineMs();
    // Allow enough time for the elected peer to fetch from origin and relay
    // one full segment. The previous ~250-900ms window was too short for
    // large segments, so origin usually won before P2P could arrive.
    const relayFloor = Math.floor(this.segmentDurationMs * 0.6);
    return Math.min(
      this.p2pDeadlineMs,
      Math.max(500, relayFloor, Math.floor(rtt * 2.0 + tuned * 0.35)),
    );
  }

  private confirmedOriginGraceMs(
    mode: P2PPlaybackMode,
    peerIds: string[],
    speculative: string[] = [],
  ): number {
    const connected = this.callbacks.getConnectedPeerCount?.() ?? 0;
    const swarmActive =
      connected >= MIN_SWARM_SIZE_PLAYBACK ||
      (this.callbacks.getSwarmSize?.() ?? 0) >= MIN_SWARM_SIZE_PREFETCH;
    if (mode === 'recover') {
      if (swarmActive && peerIds.length > 0) {
        return Math.min(
          this.p2pDeadlineMs,
          this.fetcherRelayWaitMs(peerIds, speculative),
        );
      }
      return 0;
    }
    if (mode === 'offload') return this.adaptiveGraceMs(peerIds);

    const behindLiveEdge =
      this.playbackState.latencyToLiveEdgeMs >
      this.targetLatencyMs() + this.segmentDurationMs;
    if (behindLiveEdge) return STABLE_CONFIRMED_GRACE_MS;

    if (swarmActive && !behindLiveEdge) {
      return Math.min(
        this.p2pDeadlineMs,
        this.fetcherRelayWaitMs(peerIds, speculative),
      );
    }

    const top = peerIds.length > 0 ? this.peers.get(peerIds[0]!) : undefined;
    const rtt = top?.rttMs ?? 100;
    const tuned = this.metrics.getTunedDeadlineMs();
    return Math.min(
      this.p2pDeadlineMs,
      Math.max(
        STABLE_CONFIRMED_GRACE_MS,
        LOW_LATENCY_CONFIRMED_GRACE_MS,
        Math.floor(rtt * 2.0 + tuned * 0.2),
      ),
    );
  }

  /**
   * How long a non-fetcher peer should wait for the elected origin-fetcher to
   * pull the segment and relay it over WebRTC before falling back to HTTP.
   */
  private fetcherRelayWaitMs(confirmed: string[], speculative: string[]): number {
    const peerIds = confirmed.length > 0 ? confirmed : speculative;
    let relayWait: number;
    if (peerIds.length > 0) {
      const top = this.peers.get(peerIds[0]!);
      const rtt = top?.rttMs ?? 100;
      const tuned = this.metrics.getTunedDeadlineMs();
      relayWait = Math.max(
        500,
        Math.floor(this.segmentDurationMs * 0.6),
        Math.floor(rtt * 2.0 + tuned * 0.35),
      );
    } else {
      relayWait = Math.max(
        LIVE_SPECULATIVE_GRACE_MS,
        Math.floor(this.segmentDurationMs * 0.75),
      );
    }
    // Do NOT cap by p2pDeadlineMs — a large segment routinely takes longer
    // than 1500ms to pull from the seed; capping here made every non-fetcher hit
    // the seed before the elected fetcher could relay (0% offload on HD swarms).
    // The floor scales with segment duration (see fetcherRelayFloorMs).
    const relayFloor = fetcherRelayFloorMs(this.segmentDurationMs, this.ultraLowLatency);
    return Math.min(
      liveEdgeWaitMs(this.segmentDurationMs),
      Math.max(relayWait, relayFloor),
    );
  }

  // Buffer/latency bands come from @ohmyprime/p2p-shared (single source of truth shared
  // with the video-player) so the scheduler and player can never drift. The
  // invariant recover < offload < player bufferingGoal is what keeps the
  // 'offload' band reachable so speculative-peer P2P actually engages.
  private recoverBufferThresholdMs(): number {
    return recoverBufferThresholdMs(this.segmentDurationMs, this.ultraLowLatency);
  }

  private offloadBufferThresholdMs(): number {
    const connected = this.callbacks.getConnectedPeerCount?.() ?? 0;
    const signaling = this.callbacks.getSwarmSize?.() ?? 0;
    if (connected >= MIN_SWARM_SIZE_PLAYBACK || signaling >= MIN_SWARM_SIZE_PREFETCH) {
      return swarmOffloadBufferThresholdMs(
        this.segmentDurationMs,
        this.ultraLowLatency,
        this.ultraLowLatency ? 'ultra' : 'low',
      );
    }
    return offloadBufferThresholdMs(this.segmentDurationMs, this.ultraLowLatency);
  }

  private targetLatencyMs(): number {
    return targetLatencyMs(this.segmentDurationMs, this.ultraLowLatency);
  }

  private playbackMode(): P2PPlaybackMode {
    const { bufferHealthMs, latencyToLiveEdgeMs } = this.playbackState;
    const recover = this.recoverBufferThresholdMs();
    const offload = this.offloadBufferThresholdMs();
    const target = this.targetLatencyMs();

    let mode: P2PPlaybackMode;
    if (bufferHealthMs < recover) {
      // A live-edge nudge (seek forward) drops the buffer instantly — it looks
      // identical to starvation but the stream is healthy. Suppress the recover
      // transition for one segment duration after a nudge so P2P is not
      // unnecessarily silenced while the buffer naturally refills post-seek.
      const msSinceNudge = this.lastNudgeAtMs > 0 ? Date.now() - this.lastNudgeAtMs : Infinity;
      mode = msSinceNudge < this.segmentDurationMs ? 'stable' : 'recover';
    } else if (latencyToLiveEdgeMs > target + this.segmentDurationMs) {
      mode = 'stable';
    } else if (bufferHealthMs >= offload) {
      mode = 'offload';
    } else {
      mode = 'stable';
    }

    // Diagnostics: report only real flips. recover/stable prioritize origin, so a
    // flip away from 'offload' is the usual explanation for a sudden offload-ratio
    // drop — emit the thresholds + inputs that triggered it so the curve is readable.
    if (mode !== this.lastMode) {
      const from = this.lastMode;
      this.lastMode = mode;
      this.callbacks.onModeChange?.({
        from,
        to: mode,
        bufferHealthMs,
        latencyToLiveEdgeMs,
        recoverThresholdMs: recover,
        offloadThresholdMs: offload,
        targetLatencyMs: target,
        connectedPeers: this.callbacks.getConnectedPeerCount?.() ?? 0,
        swarmSize: this.callbacks.getSwarmSize?.() ?? 0,
        offloadRatio: this.metrics.snapshot().p2pOffloadRatio,
        renditionId: this.renditionId,
      });
    }

    return mode;
  }

  private selectPeers(segmentId: string): {
    confirmed: string[];
    speculative: string[];
  } {
    const id = canonicalSegmentId(segmentId);
    // Seeds are reached only through the origin path (after the relay window),
    // never as a P2P target, so the studio uplink is the last resort.
    const allPeers = Array.from(this.peers.values()).filter(
      (p) => !this.blacklistedPeers.has(p.peerId) && !this.isSeed(p.peerId),
    );

    if (allPeers.length === 0) {
      const connectedIds = this.callbacks.getConnectedPeerIds?.() ?? [];
      const filtered = connectedIds.filter(
        (pid) => !this.blacklistedPeers.has(pid) && !this.isSeed(pid),
      );
      if (filtered.length > 0) {
        const limit = this.adaptiveParallelLimit(filtered);
        return { confirmed: [], speculative: filtered.slice(0, limit) };
      }
      return { confirmed: [], speculative: [] };
    }

    const localRegion = this.callbacks.getLocalRegion?.();
    const score = (p: PeerInfo) => {
      const regionBonus = localRegion && p.region && p.region === localRegion ? 1.5 : 1.0;
      return p.reliability * (p.uploadRate + 1) * (p.rttMs > 0 ? Math.pow(1000 / p.rttMs, 2) : 1) * regionBonus;
    };

    const sorted = [...allPeers].sort((a, b) => score(b) - score(a));
    const limit = this.adaptiveParallelLimit(sorted.map((p) => p.peerId));

    const confirmed = sorted
      .filter((p) => p.availableSegments.has(id))
      .slice(0, limit)
      .map((p) => p.peerId);

    const speculative = sorted
      .filter((p) => !p.availableSegments.has(id))
      .slice(0, limit)
      .map((p) => p.peerId);

    return { confirmed, speculative };
  }

  private findPendingRequest(
    segmentId: string,
  ): PendingSegmentRequest | undefined {
    const id = canonicalSegmentId(segmentId);
    for (const req of this.pending.values()) {
      if (canonicalSegmentId(req.segmentId) === id) {
        return req;
      }
    }
    return undefined;
  }

  private accelerateOriginFetch(
    cacheKey: string,
    req: PendingSegmentRequest,
  ): void {
    if (req.fulfilled) return;
    if (req.graceTimer) {
      clearTimeout(req.graceTimer);
      req.graceTimer = undefined;
    }
    void this.startOriginFetch(cacheKey, req, false);
  }

  private async startOriginFetch(
    cacheKey: string,
    req: PendingSegmentRequest,
    isPrefetch: boolean,
  ): Promise<void> {
    if (req.fulfilled || req.originFetchStarted) return;
    req.originFetchStarted = true;
    req.originStartedAt = Date.now();

    const originAbort = req.originAbort ?? new AbortController();
    req.originAbort = originAbort;

    const startedAt = Date.now();
    const epoch = this.catalogEpoch;
    try {
      const data = await this.fetchOriginObserved(req.segmentId, {
        signal: originAbort.signal,
        urgent: !isPrefetch,
      });
      const durationMs = Math.max(0, Date.now() - startedAt);
      if (req.fulfilled) { this.metrics.recordNetworkWaste('duplicate', data.byteLength); return; }

      const valid = await this.verifier.verify(data, req.sha256);
      if (!this.catalogIsCurrent(epoch, req.segmentId, req.sha256) || originAbort.signal.aborted) {
        this.metrics.recordNetworkWaste('canceled', data.byteLength);
        throw new Error('Playback catalogue changed');
      }
      if (!valid) { this.metrics.recordNetworkWaste('invalid', data.byteLength); throw new Error('SHA-256 mismatch from origin'); }
      if (req.fulfilled) { this.metrics.recordNetworkWaste('duplicate', data.byteLength); return; }

      this.store.set(cacheKey, data);
      const background = this.assignedRequests.get(req)?.background ?? isPrefetch;
      if (!background) {
        this.metrics.recordSegment({
          segmentId: req.segmentId,
          source: 'seed',
          durationMs,
          bytesReceived: data.byteLength,
          fromPeerId: null,
          playback: true,
        });
        this.metrics.markRequestDelivered(req.segmentId, 'seed');
        this.callbacks.onSegmentFromOrigin(req.segmentId);
      }
      if (background && this.transportVersion === 2) this.callbacks.onSegmentFromOrigin(req.segmentId);
      req.resolve(data);
    } catch (err) {
      if (req.fulfilled) return; // aborted because P2P delivered
      if (err instanceof Error && err.name === 'AbortError') return; // externally cancelled
      // Genuine origin failure (seed timeout, SHA-256 mismatch, etc.).
      // Reject the pending Promise so the player can handle it rather than hanging.
      req.fulfilled = true;
      this.pending.delete(cacheKey);
      this.reactiveRequested.delete(canonicalSegmentId(req.segmentId));
      if (!isPrefetch || this.transportVersion === 2) {
        this.metrics.recordDeadlineMiss();
        this.callbacks.onDeadlineMiss(req.segmentId);
        req.reject(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }

  private candidatePeerIds(peerIds: string[]): string[] {
    const localId = this.callbacks.getLocalPeerId?.();
    const connected = (this.callbacks.getConnectedPeerIds?.() ?? []).filter(
      (pid) => !this.isSeed(pid),
    );
    peerIds = peerIds.filter((pid) => !this.isSeed(pid));
    const pool =
      connected.length > 0
        ? connected
        : peerIds.length > 0
          ? peerIds
          : localId
            ? [localId]
            : [];
    if (localId && !pool.includes(localId)) {
      return [localId, ...pool];
    }
    return [...new Set(pool)];
  }

  private retryPendingPeerRequests(): void {
    for (const req of this.pending.values()) {
      if (req.fulfilled) continue;
      const id = canonicalSegmentId(req.segmentId);
      const { confirmed, speculative } = this.selectPeers(id);
      const targets = this.p2pRequestTargets(id, confirmed, speculative);
      for (const peerId of targets) {
        this.callbacks.requestFromPeer(peerId, id, true);
      }
    }
  }

  /**
   * A peer declined `segmentId` (NACK): ask the other viewer peers that can
   * deliver it right away (confirmed holders and the elected fetcher), skipping
   * the decliner. Returns the peers asked. No-op without a pending request.
   */
  retryPeersFor(segmentId: string, declinedBy?: string): string[] {
    const id = canonicalSegmentId(segmentId);
    const req = this.findPendingRequest(id);
    if (!req || req.fulfilled) return [];
    if (this.transportVersion === 2) {
      const state = this.assignedRequests.get(req);
      if (declinedBy && state?.active !== declinedBy) return [];
      this.advanceAssigned(req);
      return state?.active ? [state.active] : [];
    }
    const { confirmed, speculative } = this.selectPeers(id);
    const targets = this.p2pRequestTargets(id, confirmed, speculative).filter(
      (pid) => pid !== declinedBy && !this.isSeed(pid),
    );
    let sent = this.reactiveRequested.get(id);
    if (!sent) {
      sent = new Set();
      this.reactiveRequested.set(id, sent);
    }
    for (const pid of targets) {
      sent.add(pid);
      this.callbacks.requestFromPeer(pid, id, true);
    }
    return targets;
  }

  private p2pRequestTargets(
    segmentId: string,
    confirmed: string[],
    speculative: string[],
  ): string[] {
    const localId = this.callbacks.getLocalPeerId?.();
    const candidates = this.candidatePeerIds([
      ...confirmed,
      ...speculative,
    ]);
    const fetcherId = this.electFetcherPeerId(segmentId, candidates);
    const targets = new Set<string>();
    for (const pid of confirmed) targets.add(pid);
    if (fetcherId) targets.add(fetcherId);
    if (targets.size === 0) {
      for (const pid of speculative.slice(0, 2)) targets.add(pid);
    }
    if (localId) targets.delete(localId);
    if (targets.size === 0) {
      for (const pid of speculative.slice(0, 2)) {
        if (pid !== localId) targets.add(pid);
      }
    }
    return [...targets];
  }

  private isLocalOriginFetcher(segmentId: string, peerIds: string[]): boolean {
    const localId = this.callbacks.getLocalPeerId?.();
    if (!localId) return true;
    const fetcher = this.electFetcherPeerId(
      segmentId,
      this.candidatePeerIds(peerIds),
    );
    return fetcher === localId;
  }

  private raceP2PAndOrigin(
    cacheKey: string,
    segmentId: string,
    sha256: string,
    requestTargets: string[],
    confirmed: string[],
    speculative: string[],
    isPrefetch = false,
    mode: P2PPlaybackMode = this.playbackMode(),
  ): Promise<ArrayBuffer> {
    const allPeerIds = [...new Set([...confirmed, ...speculative])];
    const connectedCount = this.callbacks.getConnectedPeerCount?.() ?? 0;
    const signalingSwarm = this.callbacks.getSwarmSize?.() ?? 0;
    // WebRTC may still be (re)connecting while signaling already reports a
    // multi-peer swarm — keep deferring origin for non-fetchers.
    const swarmActive =
      connectedCount >= 1 ||
      (!isPrefetch && signalingSwarm >= 2);
    const isFetcher = this.isLocalOriginFetcher(segmentId, allPeerIds);
    const liveEdgeWait = liveEdgeWaitMs(this.segmentDurationMs);

    // Fase 1 latency fix: the configured p2pDeadlineMs now actually controls the
    // live-edge fallback window (previously it was almost completely ignored in favor
    // of the very conservative liveEdgeWaitMs = segment*2).
    // We still respect liveEdgeWait as an upper bound for safety, but let the
    // (much lower) configured deadline drive the common case.
    const baseDeadline = isPrefetch
      ? this.metrics.getTunedDeadlineMs() * PREFETCH_DEADLINE_MULTIPLIER
      : this.p2pDeadlineMs;

    const waitingForFetcherRelay =
      !isFetcher && !isPrefetch && swarmActive && confirmed.length === 0;

    const baseOriginFallbackMs = (() => {
      if (isPrefetch) {
        return Math.min(baseDeadline, liveEdgeWait);
      }
      // Non-fetchers on the live edge must wait for the elected fetcher to pull
      // + relay. The old STABLE_ORIGIN_FALLBACK_MS (800ms) cap applied to
      // everyone and caused a thundering herd — all peers hit origin before any
      // relay could complete, keeping offload stuck near 0% despite 3+ peers.
      if (waitingForFetcherRelay) {
        return this.fetcherRelayWaitMs(confirmed, speculative);
      }
      if (mode === 'stable' && swarmActive && connectedCount >= MIN_SWARM_SIZE_PREFETCH) {
        return Math.min(baseDeadline, liveEdgeWait, this.fetcherRelayWaitMs(confirmed, speculative));
      }
      if (mode === 'stable') {
        return Math.min(baseDeadline, liveEdgeWait, STABLE_ORIGIN_FALLBACK_MS);
      }
      return Math.min(baseDeadline, liveEdgeWait);
    })();

    // Buffer-aware clamp: never gamble more of the live buffer on the elected
    // fetcher's relay than we can afford without starving playback. The relay
    // wait above (≈1.5s for large segments) is fine when the buffer is fat, but at the
    // thin live edge — buffer near the recover floor — waiting that long for a
    // relay that may miss drains the buffer below recover, which flips the
    // scheduler into origin-only recover mode and collapses the rolling P2P %
    // (observed: buffer sawtoothing 2.1s↔0.13s, offload 50%→17%). So cap the
    // origin fallback by how much buffer headroom we actually have, reserving
    // enough time for the origin fetch to still land before a stall.
    //
    // Origin still races P2P *in parallel* (raceP2PAndOrigin aborts origin if
    // P2P delivers first), so a fast relay can still win — we only drop P2P's
    // *exclusive* head-start when the buffer cannot pay for it. Keeping the
    // buffer healthy is what lets the player reach the 'offload' band where the
    // full relay window applies and P2P offload climbs sustainably.
    const originFallbackMs = (() => {
      if (isPrefetch) return baseOriginFallbackMs;
      const bufferHealthMs = this.playbackState.bufferHealthMs;
      // Unknown/unreported buffer → keep prior behavior (recover-mode grace=0
      // already protects the truly-starved case).
      if (bufferHealthMs <= 0) return baseOriginFallbackMs;
      // Only gamble a full relay wait when the buffer is in the healthy 'offload'
      // band — that threshold is defined (constants.ts) as "enough buffer to
      // invest in speculative P2P", so it is exactly the line above which we can
      // afford to let the elected fetcher relay before falling back to origin.
      // There the existing anti-stampede relay window is preserved and P2P
      // offload is maximized.
      if (bufferHealthMs >= this.offloadBufferThresholdMs()) {
        return baseOriginFallbackMs;
      }
      // When explicitly waiting for the fetcher relay (swarm active, no peer
      // has confirmed holding the segment yet), never clamp the window.
      // The elected fetcher should download alone at full WAN bandwidth and
      // relay via LAN — that is strictly better than N peers splitting the
      // same WAN bandwidth with simultaneous origin fetches. Clamping here
      // causes exactly that thundering herd: with a saturated connection
      // (e.g. Cloudflare tunnel + 4 HD tabs) each peer's direct fetch
      // takes 4× longer than the single-fetcher path, keeping offload at 0%.
      if (waitingForFetcherRelay) return baseOriginFallbackMs;
      // Below the offload band the buffer is too thin (≤ ~0.7 segments) to wait
      // a full ~1.5s relay that may miss: doing so drains the buffer below the
      // recover floor, flips the scheduler into origin-only recover mode and
      // collapses the rolling P2P % (observed: buffer sawtoothing 2.1s↔0.13s,
      // offload 50%→17%). Cap the origin fallback by the buffer headroom we can
      // afford, reserving time for the origin fetch to still land before a stall.
      // Origin still races P2P *in parallel* (raceP2PAndOrigin aborts origin if
      // P2P delivers first), so a fast relay can still win — we only drop P2P's
      // *exclusive* head-start when the buffer cannot pay for it.
      const originReserveMs = Math.max(
        800,
        Math.floor(this.segmentDurationMs * 0.6),
      );
      const maxRelayGambleMs = bufferHealthMs - originReserveMs;
      // Never collapse P2P's window to 0 for non-fetchers in an active swarm.
      // When buffer is in the stable band (1000–1375ms), maxRelayGambleMs can
      // go negative because originReserveMs > bufferHealthMs. Without a floor
      // this sets originFallbackMs = 0 → origin fires immediately, racing the
      // relay from the start and winning consistently (especially with remote
      // origin where a direct fetch still takes 300-800ms — the goal is to have
      // only the elected fetcher hit origin, so every concurrent origin fetch by
      // a non-fetcher directly reduces bandwidth savings).
      //
      // Fix: always give a non-fetcher in an active swarm at least
      // LIVE_SPECULATIVE_GRACE_MS (450ms) of exclusive relay window.
      // — With remote CDN (300-800ms per fetch), the LAN relay (150-400ms)
      //   often arrives first → 1 origin hit instead of N for the segment.
      // — With localhost origin (~0ms), origin still fires at 450ms and
      //   delivers immediately → total ~450ms, safe above the 1000ms recover
      //   floor with ≥50ms headroom even at the thin edge of stable mode.
      const nonFetcherFloorMs =
        !isFetcher && swarmActive ? LIVE_SPECULATIVE_GRACE_MS : 0;
      return Math.min(
        baseOriginFallbackMs,
        Math.max(nonFetcherFloorMs, maxRelayGambleMs),
      );
    })();

    const originGraceMs = (() => {
      // Recover: do not stampede origin when the swarm is waiting on the elected
      // fetcher relay — localhost HTTP wins instantly and offload stays ~0%.
      if (mode === 'recover' && !isPrefetch) {
        if (waitingForFetcherRelay) return null;
        if (confirmed.length > 0) {
          return this.confirmedOriginGraceMs(mode, confirmed, speculative);
        }
        return 0;
      }
      // Ultra low latency mode: extremely small grace window on the live edge.
      // This is critical if we want to beat Twitch.
      if (this.ultraLowLatency && !isPrefetch) {
        return 60;
      }

      if (!isFetcher) {
        if (isPrefetch) return null;
        if (confirmed.length > 0) {
          return this.confirmedOriginGraceMs(mode, confirmed, speculative);
        }
        // Swarm active but nobody holds this segment yet — only the elected
        // fetcher should pull from origin. Defer our own origin fetch until
        // originFallbackMs (fetcherRelayWaitMs) so we do not stampede origin.
        if (swarmActive) return null;
        return LIVE_SPECULATIVE_GRACE_MS;
      }
      if (!swarmActive) return LIVE_SPECULATIVE_GRACE_MS;
      if (confirmed.length > 0) {
        return this.confirmedOriginGraceMs(mode, confirmed, speculative);
      }
      if (speculative.length > 0) {
        return LIVE_SPECULATIVE_GRACE_MS;
      }
      return LIVE_ORIGIN_LEADER_GRACE_MS;
    })();

    return new Promise<ArrayBuffer>((resolve, reject) => {
      let resolved = false;
      const originAbort = new AbortController();

      const finish = (data: ArrayBuffer) => {
        if (resolved) return;
        resolved = true;
        if (req.graceTimer) clearTimeout(req.graceTimer);
        clearTimeout(originFallbackTimer);
        clearTimeout(failTimer);
        req.fulfilled = true;
        this.pending.delete(cacheKey);
        this.reactiveRequested.delete(segmentId);
        originAbort.abort();
        resolve(data);
      };

      const req: PendingSegmentRequest = {
        segmentId,
        sha256,
        resolve: finish,
        reject,
        deadlineTimer: null as unknown as ReturnType<typeof setTimeout>,
        originAbort,
        fulfilled: false,
      };
      this.requestStartedAt.set(req, Date.now());
      this.pending.set(cacheKey, req);

      const isUrgentRequest = !isPrefetch;
      for (const peerId of requestTargets) {
        if (
          !this.callbacks.requestFromPeer(peerId, segmentId, isUrgentRequest)
        ) {
          let sent = this.reactiveRequested.get(segmentId);
          if (!sent) {
            sent = new Set();
            this.reactiveRequested.set(segmentId, sent);
          }
          sent.delete(peerId);
        }
      }

      // Always use grace timer — never fetch origin immediately.
      // This gives P2P a real window to deliver before origin races ahead.
      if (originGraceMs !== null) {
        req.graceTimer = setTimeout(() => {
          void this.startOriginFetch(cacheKey, req, isPrefetch);
        }, originGraceMs);
      }

      const originFallbackTimer = setTimeout(() => {
        if (resolved || req.fulfilled) return;
        void this.startOriginFetch(cacheKey, req, isPrefetch);
      }, originFallbackMs);

      // Fase 1: tighter overall timeout (was always +3000 on top of a 4s window).
      // Now +1.5x the effective fallback gives enough headroom while still protecting
      // the player from hanging forever.
      const failTimer = setTimeout(() => {
        if (resolved) return;
        if (req.originFetchStarted) return;
        resolved = true;
        req.fulfilled = true;
        this.pending.delete(cacheKey);
        this.reactiveRequested.delete(segmentId);
        originAbort.abort();
        this.metrics.recordDeadlineMiss();
        this.callbacks.onDeadlineMiss(segmentId);
        reject(new Error(`Segment ${segmentId} timed out from all sources`));
      }, originFallbackMs + Math.max(1500, Math.floor(this.p2pDeadlineMs * 0.6)));

      req.deadlineTimer = failTimer;
    });
  }

  private async fetchOriginObserved(segmentId: string, options: OriginFetchOptions): Promise<ArrayBuffer> {
    let received = 0;
    const source = this.origin.networkSource ?? 'unknown', path = this.origin.networkPath ?? 'unknown';
    const onBytesReceived = (bytes: number) => {
      if (!Number.isSafeInteger(bytes) || bytes <= 0) return;
      received += bytes;
      this.metrics.recordNetworkBytes(source, path, bytes);
    };
    try {
      const data = await this.origin.fetch(segmentId, { ...options, onBytesReceived });
      // Legacy/custom origins only report the completed body, never invent partial bytes.
      if (received < data.byteLength) onBytesReceived(data.byteLength - received);
      return data;
    } catch (error) {
      if (options.signal.aborted || (error instanceof Error && error.name === 'AbortError')) this.metrics.recordNetworkWaste('canceled', received);
      throw error;
    }
  }

  private async fetchFromOrigin(
    cacheKey: string,
    segmentId: string,
    sha256: string,
    signal: AbortSignal,
  ): Promise<ArrayBuffer> {
    const startedAt = Date.now();
    const epoch = this.catalogEpoch;
    const data = await this.fetchOriginObserved(segmentId, { signal, urgent: true });
    const durationMs = Math.max(0, Date.now() - startedAt);

    const valid = await this.verifier.verify(data, sha256);
    if (!this.catalogIsCurrent(epoch, segmentId, sha256) || signal.aborted) {
      this.metrics.recordNetworkWaste('canceled', data.byteLength);
      throw new Error('Playback catalogue changed');
    }
    if (!valid) {
      this.metrics.recordNetworkWaste('invalid', data.byteLength);
      throw new Error(`SHA-256 mismatch for segment ${segmentId} from origin`);
    }

    if (this.store.has(cacheKey)) this.metrics.recordNetworkWaste('duplicate', data.byteLength);
    this.store.set(cacheKey, data);

    const inflight = this.pending.get(cacheKey);
    const forPlayback = inflight !== undefined && !inflight.fulfilled;

    if (forPlayback) {
      this.metrics.recordSegment({
        segmentId,
        source: 'seed',
        durationMs,
        bytesReceived: data.byteLength,
        fromPeerId: null,
        playback: true,
      });
      this.metrics.markRequestDelivered(segmentId, 'seed');
      this.callbacks.onSegmentFromOrigin(segmentId);
    }

    if (inflight && !inflight.fulfilled) {
      if (inflight.graceTimer) clearTimeout(inflight.graceTimer);
      inflight.resolve(data);
    }

    return data;
  }

  private catalogIsCurrent(epoch: number, id: string, hash: string): boolean {
    return epoch === this.catalogEpoch &&
      (!this.callbacks.getExpectedSha256 || this.callbacks.getExpectedSha256(id) === hash);
  }
}
